import { useState, useEffect, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { callOffAPI } from '../../../lib/api';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import { usePartNumberSort } from './usePartNumberSort';
import SubTabToolbar from './SubTabToolbar';
import { useSubTabControls } from './useSubTabControls';

interface ForecastPivotItem {
  part_number: string;
  description: string;
  group_name: string;
  warehouse_name: string;
  monthly: Record<string, number>;
  weekly: Record<string, number>;
  total: number;
}

interface ForecastResponse {
  items: ForecastPivotItem[];
  total_count: number;
  page: number;
  page_size: number;
  monthly_columns: string[];
  weekly_columns: string[];
}

interface ForecastSubTabProps {
  partNumber?: string;
}

/** Format month key (2026-03) to display label (Mar-26) */
function formatMonthLabel(key: string): string {
  const [year, month] = key.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(month, 10) - 1]}-${year.slice(2)}`;
}

export default function ForecastSubTab({ partNumber }: ForecastSubTabProps) {
  const [items, setItems] = useState<ForecastPivotItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [monthlyColumns, setMonthlyColumns] = useState<string[]>([]);
  const [weeklyColumns, setWeeklyColumns] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<'monthly' | 'weekly'>('monthly');
  const { pageSize, setPageSize, searchInput, setSearchInput, search, page, setPage } = useSubTabControls(partNumber);
  const { ordering, icon: sortIcon, toggle: togglePartNumberSort } = usePartNumberSort(() => setPage(1));

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number | boolean> = { page, page_size: pageSize };
      if (search) params['search'] = search;
      if (ordering) params['ordering'] = ordering;
      const res = await callOffAPI.getForecasts(params);
      const data = res.data as unknown as ForecastResponse;
      setItems(data.items || []);
      setTotalCount(data.total_count || 0);
      setMonthlyColumns(data.monthly_columns || []);
      setWeeklyColumns(data.weekly_columns || []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load forecast data';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, ordering]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalPages = Math.ceil(totalCount / pageSize);

  const dynamicCols = viewMode === 'monthly' ? monthlyColumns : weeklyColumns;

  return (
    <div>
      <SubTabToolbar
        search={searchInput}
        onSearchChange={setSearchInput}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
      />
      {loading ? (
        <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4">No forecast data available.</p>
      ) : (
      <>
      <div className="flex items-center gap-2 mb-3">
        <Button
          variant={viewMode === 'monthly' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setViewMode('monthly')}
        >
          Monthly
        </Button>
        <Button
          variant={viewMode === 'weekly' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setViewMode('weekly')}
        >
          Weekly
        </Button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th
                className="text-left p-2 font-medium whitespace-nowrap sticky left-0 bg-muted/50 z-10 cursor-pointer hover:bg-muted/70 select-none"
                onClick={togglePartNumberSort}
              >
                <div className="flex items-center gap-1">Item No {sortIcon}</div>
              </th>
              <th className="text-left p-2 font-medium whitespace-nowrap">Item Description</th>
              <th className="text-left p-2 font-medium whitespace-nowrap">Group Name</th>
              <th className="text-left p-2 font-medium whitespace-nowrap">Warehouse</th>
              {dynamicCols.map((col) => (
                <th key={col} className="text-right p-2 font-medium whitespace-nowrap">
                  {viewMode === 'monthly' ? formatMonthLabel(col) : col}
                </th>
              ))}
              <th className="text-right p-2 font-medium whitespace-nowrap">Total</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const colData = viewMode === 'monthly' ? item.monthly : item.weekly;
              return (
                <tr key={item.part_number} className="border-b hover:bg-muted/30">
                  <td className="p-2 font-mono text-xs whitespace-nowrap sticky left-0 bg-background z-10">{item.part_number}</td>
                  <td className="p-2">{item.description}</td>
                  <td className="p-2">{item.group_name}</td>
                  <td className="p-2">{item.warehouse_name}</td>
                  {dynamicCols.map((col) => {
                    const val = colData[col] || 0;
                    return (
                      <td key={col} className={`p-2 text-right ${val > 0 ? '' : 'text-muted-foreground'}`}>
                        {val > 0 ? Math.round(val) : '-'}
                      </td>
                    );
                  })}
                  <td className="p-2 text-right font-semibold">{Math.round(item.total)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3">
          <p className="text-xs text-muted-foreground">Page {page} of {totalPages}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</Button>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
